import { formatScore } from "format"
import { useHasValueChanged } from "hooks/src/useHasValueChanged"
import { IconCertified } from "materia"
import { type CSSProperties, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  darkenPalette,
  getAccessibleTextColorForBackgroundColor,
  modifiers,
  type Palette,
  TEXT_FALLBACK,
} from "ui-tools"
import { FiestaImage, FiestaLink } from "utility-components"
import {
  getIsLeaderboardProduct,
  getProductLink,
} from "../../../dataUtilities/productDataUtilities"
import type { ProductTypeIdentifier } from "../../../graphql"
import { Badge } from "../../Badge"
import { Stars } from "../../Stars"
import { CheckpointCardIcon } from "./CheckpointCardIcon"

import s from "./CheckpointCard.module.scss"

export type CheckpointProduct = {
  id: string
  title: string | null
  image: string
  productType: ProductTypeIdentifier
  progress: {
    certified: boolean
    starsCount: number
    starsMax: number
    highScore: number
    mandatory: boolean
  }
}

// Darkens a completed card's palette; the image reads it as a brightness
const CERTIFIED_DIM = 0.35

type CheckpointCardProps = {
  product: CheckpointProduct
  isLocked: boolean
  palette: Palette
  shouldPlayUnlockAnimation: boolean
}
export const CheckpointCard = ({
  product,
  isLocked,
  palette,
  shouldPlayUnlockAnimation,
}: CheckpointCardProps) => {
  const [isFlipped, setIsFlipped] = useState(!isLocked)

  const hasChanged = useHasValueChanged(isLocked)
  const enableAnimations = hasChanged && !isLocked && shouldPlayUnlockAnimation

  return (
    <div
      className={s.card}
      style={{ "--certified-brightness": 1 - CERTIFIED_DIM } as CSSProperties}
    >
      <div
        className={modifiers(s, "card__content", {
          isFlipped,
          enableAnimations,
        })}
        onAnimationEnd={() => setIsFlipped(true)}
      >
        <CheckpointCard.Back
          productType={product.productType}
          palette={palette}
        />

        <CheckpointCard.Face product={product} palette={palette} />
      </div>
    </div>
  )
}

CheckpointCard.Back = ({
  productType,
  palette,
}: {
  productType: ProductTypeIdentifier
  palette: Palette
}) => {
  const { primary, secondary, tertiary } = palette

  return (
    <div
      className={s.cardBack}
      style={{
        backgroundColor: tertiary,
        color: primary,
        borderColor: secondary,
      }}
    >
      <CheckpointCardIcon productType={productType} />
    </div>
  )
}

CheckpointCard.Face = ({
  product,
  palette,
}: {
  product: CheckpointProduct
  palette: Palette
}) => {
  const { id, title, image, productType, progress } = product
  const { certified, starsCount, starsMax, mandatory, highScore } = progress

  const cardPalette = certified
    ? darkenPalette(palette, CERTIFIED_DIM)
    : palette

  const nameColor = getAccessibleTextColorForBackgroundColor(
    cardPalette.secondary
  )

  return (
    <FiestaLink
      to={getProductLink(id, productType)}
      className={s.cardFace}
      aria-haspopup="dialog"
      replace={true}
    >
      <FiestaImage
        className={modifiers(s, "cardFace__image", { certified })}
        src={image}
        alt=""
        loading="lazy"
        sizes="(width = small) 100px, (width = medium) 33vw, 200px"
      ></FiestaImage>

      <CardBadges certified={certified} mandatory={mandatory} />

      <div
        className={s.cardFace__bottom}
        style={{ backgroundColor: cardPalette.secondary }}
      >
        <div className={s.cardFace__progress}>
          <Stars count={starsCount} max={starsMax} />
        </div>

        <div className={s.cardFace__nameContainer} style={{ color: nameColor }}>
          <span className={s.cardFace__name}>{title || TEXT_FALLBACK}</span>
        </div>

        <div className={s.cardFace__type}>
          <ProductTag
            productType={productType}
            highScore={highScore}
            palette={cardPalette}
          />
        </div>
      </div>
    </FiestaLink>
  )
}

const CardBadges = ({
  certified,
  mandatory,
}: {
  certified: boolean
  mandatory: boolean
}) => {
  const { t } = useTranslation()

  if (!certified && !mandatory) return null

  return (
    <div className={s.cardFace__badges}>
      {mandatory && <Badge title={t("user.MANDATORY")} size="small" />}
      {certified && (
        <IconCertified size="large" title={t("product.CERTIFIED")} />
      )}
    </div>
  )
}

const ProductTag = ({
  productType,
  highScore,
  palette,
}: {
  productType: ProductTypeIdentifier
  highScore: number
  palette: Palette
}) => {
  const { t, i18n } = useTranslation()

  const isLeaderboardProduct = getIsLeaderboardProduct(productType)

  const title = isLeaderboardProduct
    ? formatScore(highScore, i18n.language, t("product.SCORE"))
    : t(`product.PRODUCT_TYPES.${productType}`)

  if (isLeaderboardProduct && highScore > 0) {
    return <Badge title={title} type="score" />
  }

  return (
    <Badge
      title={title}
      type="scoreNoPoints"
      backgroundColor={palette.tertiary}
    />
  )
}
